import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { noop, Subject } from 'rxjs';
import {
  AssignmentCustomerResponse,
  BaseButtonSize,
  BaseContractSigningTypeEnum,
  BaseCoreState,
  ContractResponse,
  BASE_PERMISSIONS,
  baseAssignmentsActions,
  baseAssignmentsSelectors,
  BaseDeregisterFormInterface,
  BaseModalService,
  BaseRolesService,
  BaseSignatureService,
  BaseSignContractOptions,
  BaseTabInterface,
  baseInquiryActions,
  baseInquirySelectors,
  ENVIRONMENTER,
  BaseDateService,
  BaseIntervalStatusType,
  BaseIntervalStatus,
  baseSharedActions,
  BaseInquiryType,
  BaseSigningStatus,
} from 'base';
import { ASSIGNMENT_MENU } from '../../../../shared/_constants/assignment-menu';
import { ExternalEmployeeService } from '../../external-employee.service';
import { BaseCustomerOfferTabsEnum } from '../../../inquiry/_enums/base-customer-offer-tabs.enum';

@Component({
  selector: 'base-customer-employee-info-modal',
  templateUrl: './base-customer-employee-info-modal.component.html',
})
export class BaseCustomerEmployeeInfoModalComponent implements OnInit, OnDestroy {
  @ViewChild('deregisterModalRef') deregisterModalRef: ElementRef;
  @ViewChild('addSignSelectionPopup') addSignSelectionPopup: ElementRef;
  @ViewChild('addSignPopup') addSignPopup: ElementRef;
  @ViewChild('inquireEmployeeModal') inquireEmployeeModal: ElementRef;

  @Input() modalRef: NgbModalRef;
  @Input() currentTab = ASSIGNMENT_MENU[0];
  @Output() currentTabChange = new EventEmitter<BaseTabInterface>();

  customerAssignment: AssignmentCustomerResponse | undefined;
  inquiry: BaseInquiryType;
  statusButtonBorder: string;
  updatedAvgRating: number;
  updatedRatingsAmount: number;
  tabs = [...ASSIGNMENT_MENU];
  buttonSizes = BaseButtonSize;
  signingStatusBySide = BaseSigningStatus;
  contractSigningType = BaseContractSigningTypeEnum;
  validContract: ContractResponse | undefined;
  hasSignContractPermission = false;
  assignmentStatus: BaseIntervalStatusType;
  deregisterForm: FormGroup<BaseDeregisterFormInterface>;
  deregisterFormSubmitted = false;
  deregistered = false;
  addSignModalRef: NgbModalRef;
  digitalSignModalRef: NgbModalRef;
  inquireEmployeeModalModalRef: NgbModalRef;
  signingType: BaseSignContractOptions | undefined;
  destroy$ = new Subject();
  customerOfferTabsEnum = BaseCustomerOfferTabsEnum;
  intervalStatus = BaseIntervalStatus;

  constructor(
    private store: Store<BaseCoreState>,
    private externalEmployeeService: ExternalEmployeeService,
    private baseRolesService: BaseRolesService,
    private fb: NonNullableFormBuilder,
    private modalService: BaseModalService,
    private baseSignatureService: BaseSignatureService,
    @Inject(ENVIRONMENTER) private environment: any
  ) {}

  get showWorkCouncilTab() {
    return (
      this.baseRolesService.hasWorkCouncilRole() ||
      this.baseRolesService.hasCustomerFeaturePermission(BASE_PERMISSIONS.FEATURE_ADMIN_WORK_COUNCIL_COMMUNICATION)
    );
  }

  ngOnInit() {
    this.hasSignContractPermission = this.baseRolesService.hasCustomerFeaturePermission(
      BASE_PERMISSIONS.FEATURE_SIGN_CONTRACT
    );

    this.store
      .select(baseAssignmentsSelectors.getCandidateReviews)
      .pipe(
        takeUntil(this.destroy$),
        filter((reviewState) => reviewState.ok === true),
        map((reviewState) => reviewState.data)
      )
      .subscribe((reviews) => {
        this.updatedAvgRating = reviews.averageRating;
        this.updatedRatingsAmount = reviews.ratings?.length;
      });

    this.store
      .select(baseAssignmentsSelectors.getAssignment)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => state.ok === true),
        map((state) => state.data)
      )
      .subscribe((assignment) => {
        this.customerAssignment = assignment;
        if (this.customerAssignment) {
          if (!assignment.offerId || !this.showWorkCouncilTab) {
            this.tabs = this.tabs.filter((tab) => tab.id !== BaseCustomerOfferTabsEnum.WorkCouncil);
          }

          this.validContract = this.customerAssignment?.contracts.find(
            (contract) => contract.valid
          ) as ContractResponse;

          this.assignmentStatus = BaseDateService.getIntervalStatus(
            this.customerAssignment.startDate,
            this.customerAssignment.endDate
          );

          if (!!this.customerAssignment.contracts.length && !!this.customerAssignment.contractFileName) {
            this.statusButtonBorder = this.externalEmployeeService.setSignButtonClass(
              this.validContract,
              this.hasSignContractPermission
            );
          }
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.store.dispatch(baseAssignmentsActions.resetAssignment());
    this.store.dispatch(baseAssignmentsActions.clearCustomerReviews());
    this.store.dispatch(baseAssignmentsActions.clearWorkCouncilCommentList());
  }

  showSignContractsModal(event: Event) {
    event.stopPropagation();

    this.openSignContractsModal();
  }

  closeModal() {
    this.modalRef.close();
  }

  onTabChange(tab: BaseTabInterface): void {
    this.currentTab = tab;
    this.currentTabChange.emit(tab);
  }

  openCandidateDeregisterModal(): void {
    if (!this.deregisterForm) {
      this.deregisterForm = this.fb.group({
        cancelationDate: ['', [Validators.required]],
        note: [''],
      });
    }

    const deregisterCandidateModalRef = this.modalService.open(
      {
        headText: 'externalEmployees.unsubscribe',
        bodyTemplate: this.deregisterModalRef,
        okText: 'action.yesSave',
        cancelText: 'action.cancel',
        canBeClosed: () => {
          this.deregisterFormSubmitted = true;
          return this.deregisterForm.valid;
        },
      },
      {
        size: 'lg',
      }
    );

    deregisterCandidateModalRef.result.then(
      () => {
        if (!this.customerAssignment) {
          return;
        }

        this.store.dispatch(
          baseAssignmentsActions.assignmentCustomerCancellation({
            payload: { assignment: this.customerAssignment.id, body: this.deregisterForm.getRawValue() },
          })
        );
        this.deregistered = true;
      },
      () => {
        deregisterCandidateModalRef.close();
      }
    );
  }

  onSignTypeChange(event: BaseSignContractOptions): void {
    this.signingType = event;
  }

  openSignContractsModal() {
    if (!this.validContract) {
      return;
    }

    const canBeSigned = this.externalEmployeeService.canBeSigned(this.hasSignContractPermission, this.validContract);

    if (!canBeSigned) {
      return;
    }

    this.store.dispatch(baseAssignmentsActions.clearContractStatus());
    this.addSignModalRef = this.baseSignatureService.openSignContractModal(this.addSignSelectionPopup);
    this.addSignModalRef.result.then(
      () => {
        if (this.customerAssignment && this.validContract) {
          this.signingType === BaseSignContractOptions.signDigital
            ? this.openDigitalSignModal()
            : this.baseSignatureService.dispatchAssignContractAction(
                this.customerAssignment.id,
                this.validContract.id,
                this.signingType,
                true
              );
        }
      },
      () => {
        this.signingType = undefined;
      }
    );
  }

  openDigitalSignModal() {
    this.digitalSignModalRef = this.externalEmployeeService.openDigitalSignModal(this.addSignPopup);
    this.digitalSignModalRef.result.then(() => noop, noop);
  }

  openInquireEmployeeModal() {
    if (!this.customerAssignment?.inquiryId) {
      return;
    }

    this.store.dispatch(baseInquiryActions.loadCustomerInquiry({ payload: this.customerAssignment.inquiryId }));
    this.store.dispatch(baseSharedActions.loadCustomerDepartmentList({ payload: {} }));

    this.store
      .select(baseInquirySelectors.getCustomerInquiry)
      .pipe(
        filter((inquiryState) => !!inquiryState.ok),
        map((inquiryState) => inquiryState.data as BaseInquiryType),
        take(1)
      )
      .subscribe((inquiry) => {
        this.inquiry = inquiry;

        this.showInquiryModal();
      });
  }

  showInquiryModal() {
    if (!this.customerAssignment) {
      return;
    }

    this.inquireEmployeeModalModalRef = this.modalService.open(
      {
        headText: 'externalEmployees.inquireSpecificEmployee',
        headTextTranslateParams: {
          employeeFullName: `${this.customerAssignment.profile.firstName} ${this.customerAssignment.profile.lastName}`,
        },
        bodyTemplate: this.inquireEmployeeModal,
        hideFooter: true,
      },
      {
        backdrop: 'static',
        size: 'lg',
      }
    );

    this.inquireEmployeeModalModalRef.result.then(noop, noop);
  }
}
